import { DEFAULT_LOCALE, type Locale } from "@/lib/i18n/config"

/** Origen de una traducción: la escribió el dueño o la generó el traductor. */
export type TranslationSource = "manual" | "auto"

/** Shape guardado en `<tabla>.translations` (espejo de items.translations). */
export type NameTranslations = Record<
  string,
  { name?: string | null; description?: string | null; source?: TranslationSource; at?: string } | null | undefined
>

export type TranslatableRow = {
  name: string
  translations?: NameTranslations | null
}

/**
 * Nombre de DISPLAY de una fila del catálogo (categoría, grupo, opción) para el
 * locale dado. La columna `name` es la fuente canónica y el contenido del locale
 * por defecto; `translations` guarda overrides por locale.
 *
 * IMPORTANTE: esto es SOLO para mostrar. El rótulo que se persiste en la orden
 * (y de ahí sale a la comanda, el KDS y el recibo) es siempre el canónico — el
 * servidor lo re-deriva de la BD, ver lib/menu/canonicalModifierName.ts.
 */
export function localizedName<T extends TranslatableRow>(row: T, locale: Locale): string {
  if (locale === DEFAULT_LOCALE || !row.translations) return row.name
  const t = row.translations[locale]
  return t?.name?.trim() || row.name
}

/**
 * Rótulo de un grupo de modificadores para el cliente. El dueño puede escribir
 * un `display_name` ("Elige tu proteína") distinto del nombre interno
 * ("Proteína Simple"); la traducción del display_name se guarda en el campo
 * `description` del override para no necesitar dos columnas.
 *
 * Precedencia: traducción del display_name → traducción del name → display_name
 * canónico → name canónico.
 */
export function localizedGroupLabel(
  group: { name: string; display_name?: string | null; translations?: NameTranslations | null },
  locale: Locale,
): string {
  const canonical = group.display_name?.trim() || group.name
  if (locale === DEFAULT_LOCALE || !group.translations) return canonical
  const t = group.translations[locale]
  if (!t) return canonical
  if (group.display_name?.trim() && t.description?.trim()) return t.description.trim()
  return t.name?.trim() || canonical
}

/**
 * `<tabla>.translations` es la columna `Json` de los tipos generados, así que
 * incluye string, number y arrays. Los helpers de arriba esperan un objeto por
 * locale. Esto estrecha en RUNTIME en vez de castear: un cast apagaría la única
 * verificación que hay y convertiría un desajuste de forma en "no traduce", en
 * silencio. Mismo criterio que `asTranslations` en lib/pos/localizeCartForDisplay.ts.
 */
export function asNameTranslations(v: unknown): NameTranslations | null {
  return typeof v === "object" && v !== null && !Array.isArray(v)
    ? (v as NameTranslations)
    : null
}

/** true si la fila ya tiene traducción utilizable para ese locale. */
export function hasTranslation(row: TranslatableRow, locale: Locale): boolean {
  if (locale === DEFAULT_LOCALE) return true
  return !!row.translations?.[locale]?.name?.trim()
}

/** true si la traducción de ese locale la escribió una persona (no se pisa). */
export function isManualTranslation(row: TranslatableRow, locale: Locale): boolean {
  return row.translations?.[locale]?.source === "manual"
}

/**
 * Mezcla una traducción nueva en el JSONB existente, preservando los demás
 * locales. Una traducción `auto` NUNCA pisa una `manual`: si el dueño corrigió
 * el texto, el motor se hace a un lado y devuelve el objeto sin tocar.
 */
export function mergeTranslation(
  existing: NameTranslations | null | undefined,
  locale: Locale,
  value: { name: string; description?: string | null },
  source: TranslationSource,
  now: string,
): NameTranslations {
  const base: NameTranslations = { ...(existing ?? {}) }
  if (source === "auto" && base[locale]?.source === "manual") return base
  base[locale] = {
    name: value.name.trim() || null,
    description: value.description?.trim() || null,
    source,
    at: now,
  }
  return base
}
